import React from 'react';
import { CalendarClock, CheckCircle2, Database, HardDrive, Radar, RefreshCw, Server, XCircle, Zap } from 'lucide-react';
import { useApi } from '../lib/useApi';
import { ago, bytes, num } from '../lib/format';
import { Bar, Button, Card, Chip, ErrorBanner, PageHeader, PageSkeleton, Stat } from '../components/ui';

interface Check { ok: boolean; ms?: number; status?: number; error?: string; url?: string }
interface Resp {
  checks: { database: Check; fluxwatch_api: Check; redis: Check; scheduler: Check };
  database: { max_connections: number; connections: number; active: number; idle_in_transaction: number; slow_queries: number;
              size_bytes: number; version: string; latency_ms: number; by_app: { app: string; n: number }[]; largest_tables: { table: string; bytes: number; rows: number }[] };
  pipeline: { scans_1h: number; scans_24h: number; last_scan_at: string | null; active_schedules: number; overdue_schedules: number };
}

const CHECKS: { key: keyof Resp['checks']; label: string; icon: React.ElementType; ok: string; bad: string }[] = [
  { key: 'database', label: 'Database', icon: Database, ok: 'Connected, plenty of connections free', bad: 'Over 80% of connections in use' },
  { key: 'fluxwatch_api', label: 'FluxWatch API', icon: Server, ok: 'Responding', bad: "Not responding — customers can't use the app" },
  { key: 'redis', label: 'Scan queue (Redis)', icon: Zap, ok: 'Responding', bad: 'Unreachable — scans and live updates stop' },
  { key: 'scheduler', label: 'Scan scheduler', icon: CalendarClock, ok: 'No overdue scheduled scans', bad: 'Scheduled scans are overdue by over an hour' },
];

export default function Health() {
  const { data, error, loading, reload, refreshing } = useApi<Resp>('/health', 30000);
  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorBanner text={error || 'Could not load system health'} onRetry={reload} />;
  const db = data.database;
  const allOk = Object.values(data.checks).every(c => c.ok);
  const connPct = Math.round(db.connections / db.max_connections * 100);

  return (
    <div>
      <PageHeader title="System health" subtitle={allOk ? 'Everything is running normally' : 'Something needs attention'}
        actions={<Button icon={RefreshCw} loading={refreshing} onClick={reload}>Refresh</Button>} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {CHECKS.map(c => {
          const r = data.checks[c.key];
          return (
            <div key={c.key} className={`rounded-xl border bg-white p-4 ${r.ok ? 'border-slate-200' : 'border-red-200'}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-900"><c.icon className="h-4 w-4 text-slate-400" />{c.label}</span>
                {r.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
              </div>
              <div className={`mt-2 text-sm ${r.ok ? 'text-slate-600' : 'text-red-700'}`}>{r.ok ? c.ok : (r.error ? `${c.bad} (${r.error})` : c.bad)}</div>
              {r.ms != null && <div className="mt-1 text-xs text-slate-400">{r.ms} ms{r.url ? ` · ${r.url}` : ''}</div>}
            </div>
          );
        })}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Scans in the last hour" value={num(data.pipeline.scans_1h)} icon={Radar} tone="green" sub={`${num(data.pipeline.scans_24h)} in 24 h · last ${ago(data.pipeline.last_scan_at)}`} />
        <Stat label="Scheduled vendors" value={num(data.pipeline.active_schedules)} icon={CalendarClock} tone={data.pipeline.overdue_schedules ? 'amber' : 'teal'} sub={`${num(data.pipeline.overdue_schedules)} overdue`} />
        <Stat label="Database size" value={bytes(db.size_bytes)} icon={HardDrive} tone="purple" sub={`${db.latency_ms} ms query latency`} />
        <Stat label="Slow queries" value={num(db.slow_queries)} icon={Database} tone={db.slow_queries ? 'red' : 'slate'} sub={`running over a minute · ${num(db.idle_in_transaction)} stuck in a transaction`} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card title="Database connections" icon={Database} tone="blue" right={<Chip tone={connPct >= 80 ? 'red' : connPct >= 60 ? 'amber' : 'green'}>{db.connections} of {db.max_connections}</Chip>}>
          <Bar pct={connPct} />
          <div className="mt-1 text-xs text-slate-500">{db.active} running a query right now. The admin app holds at most 8.</div>
          <ul className="mt-4 space-y-2">
            {db.by_app.map(a => (
              <li key={a.app} className="flex items-center justify-between text-sm"><span className="font-mono text-xs text-slate-600">{a.app}</span><span className="font-medium text-slate-900">{a.n}</span></li>
            ))}
          </ul>
        </Card>
        <Card title="Largest tables" icon={HardDrive} tone="purple" bodyClass="p-0">
          <ul className="divide-y divide-slate-100">
            {db.largest_tables.map(t => {
              const max = db.largest_tables[0]?.bytes || 1;
              return (
                <li key={t.table} className="px-5 py-2.5">
                  <div className="flex items-center justify-between text-sm"><span className="font-mono text-xs text-slate-700">{t.table}</span><span className="text-slate-900">{bytes(t.bytes)} <span className="text-xs text-slate-400">· {num(t.rows)} rows</span></span></div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-400" style={{ width: `${t.bytes / max * 100}%` }} /></div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
      <p className="mt-4 text-xs text-slate-400">{db.version.split(',')[0]} · refreshes every 30 seconds</p>
    </div>
  );
}
